/**
 * WhatsApp Meta Embedded Signup — frontend launcher.
 * Purpose:  load the Facebook JS SDK, run the Embedded Signup popup, and return
 *           the one-time { code, waba_id, phone_number_id } for the BACKEND to
 *           exchange. The App Secret + the code→token exchange NEVER happen here.
 * Security: only the PUBLIC App ID + config_id touch the browser (env-driven).
 *           The popup payload is accepted only from a facebook.com origin.
 */

const SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
let sdkPromise = null;

function loadFbSdk(appId, version) {
  if (typeof window !== 'undefined' && window.FB) return Promise.resolve(window.FB);
  if (sdkPromise) return sdkPromise;
  sdkPromise = new Promise((resolve, reject) => {
    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: false, version });
      resolve(window.FB);
    };
    const s = document.createElement('script');
    s.src = SDK_SRC; s.async = true; s.defer = true; s.crossOrigin = 'anonymous';
    s.onerror = () => { sdkPromise = null; reject(new Error('sdk_load_failed')); };
    document.body.appendChild(s);
  });
  return sdkPromise;
}

/**
 * Parse an Embedded Signup postMessage.
 * @returns {{waba_id?:string, phone_number_id?:string}|null} on a FINISH event, else null.
 * security: rejects any message whose origin is not a facebook.com host.
 */
export function parseEsMessage(event) {
  let host;
  try { host = new URL(event.origin).hostname; } catch (_) { return null; }
  if (!/(^|\.)facebook\.com$/.test(host)) return null;
  let data;
  try { data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data; }
  catch (_) { return null; }
  if (data && data.type === 'WA_EMBEDDED_SIGNUP' && data.event === 'FINISH') {
    return { waba_id: data.data?.waba_id, phone_number_id: data.data?.phone_number_id };
  }
  return null;
}

/**
 * launchWhatsAppEmbeddedSignup
 * @returns {Promise<{code:string, waba_id?:string, phone_number_id?:string}>}
 * @throws {Error} 'not_configured' | 'cancelled' | 'login_failed' | 'sdk_load_failed'
 */
export async function launchWhatsAppEmbeddedSignup() {
  const appId = process.env.REACT_APP_META_APP_ID;
  const configId = process.env.REACT_APP_META_ES_CONFIG_ID;
  const version = process.env.REACT_APP_META_GRAPH_VERSION || 'v21.0';
  if (!appId || !configId) throw new Error('not_configured');

  const FB = await loadFbSdk(appId, version);

  let session = null;
  const onMessage = (event) => { const s = parseEsMessage(event); if (s) session = s; };
  window.addEventListener('message', onMessage);
  try {
    const code = await new Promise((resolve, reject) => {
      FB.login((response) => {
        const c = response && response.authResponse && response.authResponse.code;
        if (c) resolve(c);
        else reject(new Error(response && response.status === 'unknown' ? 'cancelled' : 'login_failed'));
      }, {
        config_id: configId,
        response_type: 'code',
        override_default_response_type: true,
        extras: { setup: {}, featureType: '', sessionInfoVersion: '3' },
      });
    });
    return { code, waba_id: session && session.waba_id, phone_number_id: session && session.phone_number_id };
  } finally {
    window.removeEventListener('message', onMessage);
  }
}
